/**
 * /drip/templates - Onboarding drip-email template manager (OCP-85).
 *
 * Lists the active drip-templates (one per stage x locale x variant,
 * latest version wins). Click a row to edit subject + body; PATCH
 * creates a new version + deactivates the old. Older versions stay
 * queryable from drip_email_log entries for audit.
 */
import { useEffect, useState } from 'react';

import { ApiError, api } from '../lib/api';

interface DripTemplate {
  id: string;
  stage: string;
  locale: string;
  variant: string;
  version: number;
  subject: string;
  body_markdown: string;
  active: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

const STAGE_LABEL: Record<string, string> = {
  day_1: 'Day 1 - welcome',
  day_3: 'Day 3 - invite teammates',
  day_7: 'Day 7 - feature intro',
  day_14: 'Day 14 - upgrade nudge',
};

const LOCALE_LABEL: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
};

export function AdminDripTemplatesPage() {
  const [items, setItems] = useState<DripTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DripTemplate | null>(null);

  const load = async () => {
    try {
      const r = await api<{ items: DripTemplate[] }>('/v1/admin/drip/templates');
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Could not load drip templates.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Drip templates</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Onboarding email sequence sent day 1 / 3 / 7 / 14 after a tenant
          provisions. Editing creates a new version - older versions stay
          referenced from the per-tenant log.
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">Loading templates...</p>}

      {items && (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Stage</th>
              <th className="px-3 py-2">Locale</th>
              <th className="px-3 py-2">Variant</th>
              <th className="px-3 py-2">Version</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Updated by</th>
              <th className="px-3 py-2">Updated at</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} data-testid={`drip-template-${t.stage}-${t.locale}-${t.variant}`} className="border-b border-gray-200 dark:border-gray-800">
                <td className="px-3 py-2 font-medium">{STAGE_LABEL[t.stage] ?? t.stage}</td>
                <td className="px-3 py-2">{LOCALE_LABEL[t.locale] ?? t.locale}</td>
                <td className="px-3 py-2">{t.variant}</td>
                <td className="px-3 py-2">v{t.version}</td>
                <td className="px-3 py-2 max-w-md truncate">{t.subject}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{t.updated_by ?? 'seed'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{formatDate(t.updated_at)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60"
                    onClick={() => setEditing(t)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {editing && (
        <EditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function EditModal({
  template,
  onClose,
  onSaved,
}: {
  template: DripTemplate;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_markdown);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api(`/v1/admin/drip/templates/${template.id}`, {
        method: 'PATCH',
        body: { subject, body_markdown: body },
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50 px-4">
      <div className="w-full max-w-3xl rounded-lg bg-white dark:bg-gray-900 p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Edit {STAGE_LABEL[template.stage] ?? template.stage} ({LOCALE_LABEL[template.locale] ?? template.locale})
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
          >
            x
          </button>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Currently v{template.version}. Saving creates v{template.version + 1}.
          Variables: {'{{workspace_name}}'}, {'{{workspace_url}}'}, {'{{operator_name}}'}, {'{{unsubscribe_url}}'}.
        </p>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm"
            data-testid="drip-edit-subject"
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">Body (markdown)</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-3 py-2 font-mono text-xs"
            data-testid="drip-edit-body"
          />
        </label>
        {error && (
          <div role="alert" className="mb-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-800 dark:text-red-300">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="drip-edit-save"
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save new version'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
